/**
 * Геометрія обрізання фото в круглий кадр (вікно PhotoCropModal).
 * Кадр — квадрат viewport×viewport; зображення масштабується так, щоб завжди
 * повністю його покривати (cover), зум множить цей масштаб. x/y — зсув лівого
 * верхнього кута зображення відносно кадру (≤ 0).
 */

export interface CropState {
    zoom: number
    x: number
    y: number
}

export const MIN_ZOOM = 1
export const MAX_ZOOM = 4

/** Масштаб, за якого менша сторона зображення дорівнює кадру */
export const coverScale = (width: number, height: number, viewport: number): number =>
    viewport / Math.min(width, height)

export const clampZoom = (zoom: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))

/** Розмір зображення на екрані за поточного зуму */
export const displaySize = (width: number, height: number, viewport: number, zoom: number) => {
    const scale = coverScale(width, height, viewport) * zoom
    return { width: width * scale, height: height * scale }
}

/** Зсув, за якого зображення не відкриває порожнечу в кадрі */
export const clampOffset = (state: CropState, width: number, height: number, viewport: number): CropState => {
    const size = displaySize(width, height, viewport, state.zoom)
    const clamp = (value: number, min: number) => Math.min(0, Math.max(min, value))
    return { zoom: state.zoom, x: clamp(state.x, viewport - size.width), y: clamp(state.y, viewport - size.height) }
}

/** Початковий стан: зум 1, зображення по центру кадру */
export const initialCrop = (width: number, height: number, viewport: number): CropState => {
    const size = displaySize(width, height, viewport, MIN_ZOOM)
    return { zoom: MIN_ZOOM, x: (viewport - size.width) / 2, y: (viewport - size.height) / 2 }
}

/**
 * Новий зум так, щоб точка під anchor (за замовчуванням центр кадру) лишилась на місці.
 */
export const zoomTo = (
    state: CropState,
    nextZoom: number,
    width: number,
    height: number,
    viewport: number,
    anchor: { x: number; y: number } = { x: viewport / 2, y: viewport / 2 }
): CropState => {
    const zoom = clampZoom(nextZoom)
    const ratio = zoom / state.zoom
    return clampOffset(
        { zoom, x: anchor.x - (anchor.x - state.x) * ratio, y: anchor.y - (anchor.y - state.y) * ratio },
        width,
        height,
        viewport
    )
}

/** Прямокутник у пікселях вихідного зображення, що потрапляє в кадр */
export const cropRect = (state: CropState, width: number, height: number, viewport: number) => {
    const scale = coverScale(width, height, viewport) * state.zoom
    const size = Math.min(viewport / scale, width, height)
    const sx = Math.min(Math.max(0, -state.x / scale), width - size)
    const sy = Math.min(Math.max(0, -state.y / scale), height - size)
    return { sx, sy, size }
}
